import IMAGE_VARIANTS from "../constant/imageVariants.json";

const ASSETS = "/assets";
const BASE = `${ASSETS}/categoriesData`;
export const FALLBACK_IMAGE = `${BASE}/default.png`;

const encodePath = (filePath) =>
  filePath.split("/").map(encodeURIComponent).join("/");

// Must list the same extensions as SOURCE_PATTERN in scripts/generate-webp.js:
// that script decides which sources become .webp, and this decides which URLs
// point at one. When .jfif was converted there but not here, the srcset below
// emitted the untouched .jfif twice under two different `w` descriptors.
const toWebp = (filePath) => filePath.replace(/\.(png|jpe?g|jfif)$/i, ".webp");

/**
 * Builds the public URL for a catalogue image.
 *
 * Several filenames contain spaces, "&" or brackets (e.g.
 * "Fire_Extinguishers_(Type_A_B_C).png"). Those are legal on disk but have to
 * be percent-encoded before they are used as a URL — otherwise they break as
 * soon as the same path is handed to a crawler, an og:image tag or JSON-LD.
 *
 * PNG and JPEG sources resolve to the .webp that scripts/generate-webp.js
 * writes next to them during prebuild — around a quarter of the bytes for the
 * same visual quality. SVGs are already compact and vector, so they pass
 * through untouched. Use productImageSource for the original file.
 */
export const productImageUrl = (image) =>
  encodePath(toWebp(`${BASE}/${image || "default.png"}`));

/**
 * The `srcset` for a catalogue image, so the browser can pick a copy that suits
 * the display box instead of always taking the ~1000px original. Returns
 * undefined for anything without variants — SVGs, and any image added to the
 * catalogue since the last prebuild.
 */
export const productImageSrcSet = (image) =>
  assetSrcSet(`categoriesData/${image || "default.png"}`);

/**
 * The `srcset` for any image under public/assets that the build generated width
 * variants for — the catalogue and the certificate scans today.
 *
 * `assetPath` is the path relative to public/assets, which is also the key
 * scripts/generate-webp.js writes into the manifest ("certificates/ISO.jpg").
 * Widths come from that manifest rather than being guessed here: a `w`
 * descriptor has to state the file's real intrinsic width, and only the resize
 * step knows it. The last width in each list is the full-size file; the rest
 * are the "-<width>.webp" variants.
 *
 * Returns undefined when there are no variants. React drops an undefined
 * srcset, leaving the plain src, so an unrecognised path degrades to a
 * full-size image rather than a broken one.
 */
export const assetSrcSet = (assetPath) => {
  const widths = IMAGE_VARIANTS[assetPath];
  if (!widths || widths.length < 2) return undefined;

  const fullSize = toWebp(`${ASSETS}/${assetPath}`);
  const largest = widths[widths.length - 1];

  return widths
    .map((width) => {
      const file =
        width === largest
          ? fullSize
          : fullSize.replace(/\.webp$/, `-${width}.webp`);
      return `${encodePath(file)} ${width}w`;
    })
    .join(", ");
};

/** The .webp URL for any image under public/assets, given its relative path. */
export const assetImageUrl = (assetPath) =>
  encodePath(toWebp(`${ASSETS}/${assetPath}`));

/**
 * The original PNG/JPEG, kept for the <img> fallback and for og:image — some
 * social crawlers still preview WebP unreliably.
 */
export const productImageSource = (image) =>
  encodePath(`${BASE}/${image || "default.png"}`);

/**
 * onError handler for catalogue images.
 *
 * srcset has to be cleared before src is reassigned. The browser picks what to
 * display from srcset whenever one is set, and re-runs that selection on
 * change — so assigning src alone leaves the failed candidate in place and the
 * fallback never appears. This was harmless while no image had a srcset; it
 * stops being harmless the moment one does, hence the shared helper rather than
 * the same three lines copied into four components.
 */
export const applyImageFallback = (event) => {
  const img = event.currentTarget;
  if (img.src.endsWith(FALLBACK_IMAGE)) return;
  img.srcset = "";
  img.sizes = "";
  img.src = FALLBACK_IMAGE;
};
